import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

/**
 * Button — Secure Labs Design System (UIP-01)
 *
 * Variants: primary (default), secondary, danger, ghost, link
 * Sizes: lg (48px, primary CTAs), default/md (40px, toolbar), sm (32px, table row), icon sizes
 *
 * Brand signature: Copper Sun glow on primary buttons per Brand Book § 2.5.
 * Focus ring: 2px solid var(--text-accent) with 2px offset per DS-09.
 */
const buttonVariants = cva(
  [
    'group/button inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap',
    'font-semibold text-[0.9375rem] leading-none select-none',
    'transition-all duration-[var(--duration-instant)] ease-[var(--ease-standard)]',
    'outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-page)]',
    'active:not-aria-[haspopup]:translate-y-px active:not-aria-[haspopup]:scale-[0.98]',
    'disabled:pointer-events-none disabled:cursor-not-allowed',
    'aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-5',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary — Copper Sun CTA with brand glow (UIP-01.3)
        primary: [
          'bg-[var(--cta-primary-bg)] text-[var(--cta-primary-text)]',
          'shadow-[var(--cta-primary-shadow)]',
          'hover:bg-[var(--cta-primary-bg-hover)] hover:shadow-[0px_6px_28px_rgba(244,162,97,0.50)]',
          'active:not-aria-[haspopup]:shadow-none',
          'disabled:bg-[var(--cta-primary-bg)]/28 disabled:text-[var(--cta-primary-text)]/40 disabled:shadow-none',
          'aria-disabled:bg-[var(--cta-primary-bg)]/28 aria-disabled:text-[var(--cta-primary-text)]/40 aria-disabled:shadow-none',
        ].join(' '),
        // Backwards compat alias — pages using variant="default" get primary styling
        default: [
          'bg-[var(--cta-primary-bg)] text-[var(--cta-primary-text)]',
          'shadow-[var(--cta-primary-shadow)]',
          'hover:bg-[var(--cta-primary-bg-hover)] hover:shadow-[0px_6px_28px_rgba(244,162,97,0.50)]',
          'active:not-aria-[haspopup]:shadow-none',
          'disabled:bg-[var(--cta-primary-bg)]/28 disabled:text-[var(--cta-primary-text)]/40 disabled:shadow-none',
          'aria-disabled:bg-[var(--cta-primary-bg)]/28 aria-disabled:text-[var(--cta-primary-text)]/40 aria-disabled:shadow-none',
        ].join(' '),
        // Secondary — ghost surface with border (UIP-01.4)
        secondary: [
          'bg-[var(--cta-secondary-bg)] text-[var(--cta-secondary-text)]',
          'border border-[var(--cta-secondary-border)]',
          'hover:bg-[rgba(245,238,222,0.10)] hover:border-[rgba(245,238,222,0.22)]',
          'disabled:bg-[rgba(245,238,222,0.03)] disabled:text-[var(--cta-secondary-text)]/28 disabled:border-[rgba(245,238,222,0.07)]',
          'aria-disabled:bg-[rgba(245,238,222,0.03)] aria-disabled:text-[var(--cta-secondary-text)]/28 aria-disabled:border-[rgba(245,238,222,0.07)]',
        ].join(' '),
        // Danger — destructive actions (UIP-01.5)
        danger: [
          'bg-[var(--cta-danger-bg)] text-[var(--cta-danger-text)]',
          'border border-[var(--cta-danger-border)]',
          'hover:bg-[rgba(248,113,113,0.22)] hover:border-[rgba(248,113,113,0.50)]',
          'focus-visible:ring-[var(--status-red)]',
          'disabled:bg-[rgba(248,113,113,0.08)] disabled:text-[var(--cta-danger-text)]/40 disabled:border-[rgba(248,113,113,0.15)]',
          'aria-disabled:bg-[rgba(248,113,113,0.08)] aria-disabled:text-[var(--cta-danger-text)]/40 aria-disabled:border-[rgba(248,113,113,0.15)]',
        ].join(' '),
        // Backwards compat alias for pages using variant="destructive"
        destructive: [
          'bg-[var(--cta-danger-bg)] text-[var(--cta-danger-text)]',
          'border border-[var(--cta-danger-border)]',
          'hover:bg-[rgba(248,113,113,0.22)] hover:border-[rgba(248,113,113,0.50)]',
          'focus-visible:ring-[var(--status-red)]',
          'disabled:bg-[rgba(248,113,113,0.08)] disabled:text-[var(--cta-danger-text)]/40 disabled:border-[rgba(248,113,113,0.15)]',
          'aria-disabled:bg-[rgba(248,113,113,0.08)] aria-disabled:text-[var(--cta-danger-text)]/40 aria-disabled:border-[rgba(248,113,113,0.15)]',
        ].join(' '),
        // Ghost — tertiary / inline (UIP-01.6)
        ghost: [
          'text-[var(--text-accent)]',
          'hover:bg-[rgba(245,238,222,0.07)]',
          'aria-expanded:bg-[rgba(245,238,222,0.07)]',
        ].join(' '),
        // Outline — kept for shadcn compat (maps to secondary visually)
        outline: [
          'bg-[var(--cta-secondary-bg)] text-[var(--cta-secondary-text)]',
          'border border-[var(--cta-secondary-border)]',
          'hover:bg-[rgba(245,238,222,0.10)] hover:border-[rgba(245,238,222,0.22)]',
          'disabled:bg-[rgba(245,238,222,0.03)] disabled:text-[var(--cta-secondary-text)]/28 disabled:border-[rgba(245,238,222,0.07)]',
        ].join(' '),
        // Link — text-only with underline
        link: 'text-[var(--text-accent)] underline-offset-4 hover:underline',
      },
      size: {
        // lg = 48px — primary CTAs, form submit, modal confirm (UIP-01.2)
        lg: 'h-12 px-6 rounded-[10px] text-[0.9375rem]',
        // default/md = 40px — toolbar actions, secondary buttons
        default: 'h-10 px-5 rounded-[10px] text-[0.8125rem]',
        // sm = 32px — table row actions, inline actions
        sm: 'h-8 px-4 rounded-[8px] text-[0.6875rem]',
        // xs = 24px — compact inline
        xs: 'h-6 px-2 rounded-[6px] text-[0.6875rem] gap-1 [&_svg:not([class*="size-"])]:size-3',
        // Icon sizes — square buttons (UIP-01.7)
        icon: 'size-10 rounded-[10px]',
        'icon-sm': 'size-8 rounded-[8px]',
        'icon-xs': 'size-6 rounded-[6px] [&_svg:not([class*="size-"])]:size-3',
        'icon-lg': 'size-12 rounded-[10px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * Inline loading spinner — 16px, matches button text colour.
 * Renders in place of the leading icon slot when isLoading is true.
 */
function ButtonSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('size-4 animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  isLoading = false,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows an inline spinner and disables interaction. Label remains visible. */
    isLoading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  // When asChild is true, Radix Slot expects exactly one React element child.
  // We skip the spinner injection in that case (asChild is for link-as-button
  // patterns where loading state is not applicable).
  const content = asChild ? (
    children
  ) : (
    <>
      {isLoading && <ButtonSpinner />}
      {children}
    </>
  );

  // isDisabled uses boolean OR — intentional: button is disabled when loading
  // OR when the caller explicitly disables it. ?? would not work here because
  // isLoading=false (not undefined) would fall through to props.disabled.
  const isDisabled = isLoading === true || props.disabled === true;

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-disabled={isDisabled || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      disabled={asChild ? undefined : isDisabled}
    >
      {content}
    </Comp>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
